import type { Unsubscribe } from './query.ts';

export interface AuthUser {
  readonly uid: string;
  readonly email: string | null;
  readonly displayName: string | null;
  readonly photoUrl: string | null;
  readonly emailVerified: boolean;
}

export type MemberKind = 'internal' | 'sub' | 'client';

/** A user's relationship to one company. A person may hold several. */
export interface Membership {
  readonly companyId: string;
  readonly companyName: string;
  readonly uid: string;
  readonly kind: MemberKind;
  readonly roleId: string;
  readonly jobAccess: 'all' | 'assigned';
  readonly jobIds: readonly string[];
  readonly status: 'invited' | 'active' | 'inactive';
  /**
   * The contact this membership came from, for a sub or a client.
   *
   * Carried here because a sub's own confirmation is keyed by contact id, so
   * without it the sub portal cannot tell which answer on an item is theirs
   * — and it must never be taken from a request, since answering for another
   * sub is the obvious thing to try.
   */
  readonly contactId?: string;
}

export interface AuthPort {
  currentUser(): Promise<AuthUser | null>;
  onAuthStateChanged(listener: (user: AuthUser | null) => void): Unsubscribe;
  signInWithEmail(email: string, password: string): Promise<AuthUser>;
  signUpWithEmail(email: string, password: string, displayName: string): Promise<AuthUser>;
  signInWithProvider(provider: 'google'): Promise<AuthUser>;
  sendPasswordReset(email: string): Promise<void>;
  sendMagicLink(email: string, redirectUrl: string): Promise<void>;
  signOut(): Promise<void>;
  /** Memberships for the signed-in user, across every company. */
  listMemberships(): Promise<readonly Membership[]>;
  activeCompanyId(): Promise<string | null>;
  setActiveCompany(companyId: string): Promise<void>;
}

export class AuthError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}
